import { Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import { KycActionButtons } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/component/KycActionButtons';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	AcceptanceStatus,
	DataKYCFileTctWithFile,
	GetKYCInvestorInstanceRes,
} from '@dealstack/shared/lib/generated';
import { useUpdateKycDeclarationsStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/api/useUpdateKycDeclarationsStatus';
import { Step } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { useManageKYCController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/useManageKYCController';
import { useSearchParams } from 'react-router-dom';
import { isStepInherited } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepTabs/utils/isStepInherited';
import { DecisionCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/DecisionCard';
import { RejectionConfirmation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/RejectionConfirmation';
import { getDecisionAudit } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/utils/getDecisionAudit';
import { useRejectionState } from '@dealstack/frontend/shared/hooks/useRejectionState';

interface Props {
	currentInvestor: GetKYCInvestorInstanceRes | undefined;
	currentStep: Step;
	selectedKycFile: DataKYCFileTctWithFile | undefined;
	isReadOnly?: boolean;
}

export const ReviewDeclarations = ({
	currentInvestor,
	currentStep,
	selectedKycFile,
	isReadOnly = false,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;
	const { getKYCDataRequest } = useManageKYCController(currentInvestor?.id, kycWorkflowId);

	const { updateKycDeclarationStatus, mutation } =
		useUpdateKycDeclarationsStatus(getKYCDataRequest);

	const isInherited = isStepInherited(currentStep, currentInvestor);
	const isLoading = mutation.isPending || getKYCDataRequest.isFetching;

	const checkedDeclarations = useMemo(
		() => currentInvestor?.declarations?.filter((d) => d.checked) ?? [],
		[currentInvestor?.declarations]
	);

	const overallStatus = useMemo<AcceptanceStatus>(() => {
		if (!checkedDeclarations.length) {
			return AcceptanceStatus.PENDING;
		}
		if (checkedDeclarations.every((d) => d.status === AcceptanceStatus.APPROVED)) {
			return AcceptanceStatus.APPROVED;
		}
		if (
			checkedDeclarations.some(
				(d) =>
					d.status === AcceptanceStatus.REJECTED ||
					d.status === AcceptanceStatus.REREQUESTED
			)
		) {
			return AcceptanceStatus.REJECTED;
		}
		return AcceptanceStatus.PENDING;
	}, [checkedDeclarations]);

	const rejectionComment = useMemo(() => {
		const rejected = checkedDeclarations.find(
			(d) =>
				d.status === AcceptanceStatus.REJECTED || d.status === AcceptanceStatus.REREQUESTED
		);
		return rejected?.rejectionComment ?? null;
	}, [checkedDeclarations]);

	const audit = useMemo(() => {
		const events = checkedDeclarations.flatMap((d) => d.kycEventLog ?? []);
		return getDecisionAudit(events);
	}, [checkedDeclarations]);

	const {
		isRejecting,
		comment,
		commentError,
		handleCommentChange,
		handleConfirmReject,
		handleCancelReject,
		handleDecline,
		reset,
	} = useRejectionState({
		emptyCommentError: tMipManage('kyc.review.add_comment_error'),
		onConfirm: async (declarationsRejectionComment) => {
			await updateKycDeclarationStatus({
				investorId: currentInvestor?.id ?? '',
				body: {
					status: AcceptanceStatus.REREQUESTED,
					rejectionComment: declarationsRejectionComment,
				},
				kycWorkflowId,
			});
		},
	});

	const handleApprove = async () => {
		await updateKycDeclarationStatus({
			investorId: currentInvestor?.id ?? '',
			body: { status: AcceptanceStatus.APPROVED, rejectionComment: '' },
			kycWorkflowId,
		});
		reset();
	};

	const isApproved = overallStatus === AcceptanceStatus.APPROVED;
	const isRejected = overallStatus === AcceptanceStatus.REJECTED;

	if (isApproved || isRejected) {
		return (
			<DecisionCard
				status={overallStatus}
				auditedBy={audit.auditedBy}
				timestamp={audit.timestamp}
				rejectionComment={rejectionComment}
				onUpdateDecision={async () => {
					await updateKycDeclarationStatus({
						investorId: currentInvestor?.id ?? '',
						body: { status: AcceptanceStatus.PENDING, rejectionComment: '' },
						kycWorkflowId,
					});
					reset();
				}}
				isLoading={isLoading}
				disabled={isReadOnly || isInherited}
			/>
		);
	}

	return (
		<Flex direction="column" gap="1rem">
			<KycActionButtons
				currentInvestor={currentInvestor}
				currentStep={currentStep}
				selectedKycFile={selectedKycFile}
				loading={isLoading}
				isDisabled={isReadOnly || isInherited}
				onApprove={handleApprove}
				onDecline={handleDecline}
			/>
			{isInherited && (
				<Flex align="center" gap="0.5rem" c="dark.2">
					<Icon variant="clock-time-watch" />
					<Text size="B6">{tMipManage('kyc.review.inherited_file_hint')}</Text>
				</Flex>
			)}
			{isRejecting && !isReadOnly && (
				<RejectionConfirmation
					comment={comment}
					commentError={commentError}
					isLoading={isLoading}
					disabled={isInherited}
					onCommentChange={handleCommentChange}
					onCancel={handleCancelReject}
					onConfirm={handleConfirmReject}
				/>
			)}
		</Flex>
	);
};
